/* Reeva Europe — offer ladder (best value selector)
   Keeps the product form's [name="id"] input in sync with the selected tier
   (which may be a different product than the page's product), updates the
   buy-button price, sticky bar, urgency countdown and stock indicator.
   Tiers are grouped by pack quantity; a group can hold multiple color
   products, switched via the color dropdown. */
(function () {
  if (customElements.get('reeva-offer-ladder')) return;

  class ReevaOfferLadder extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onChange.bind(this));
      this.tiers = Array.from(this.querySelectorAll('[data-offer-tier]'));
      this.startCountdown();
      // Defer the first sync until the theme's ProductForm has initialised
      // (it re-enables the disabled id input in its constructor).
      requestAnimationFrame(() => this.syncActiveTier());
    }

    disconnectedCallback() {
      if (this.countdownInterval) clearInterval(this.countdownInterval);
    }

    get form() {
      return document.forms[this.getAttribute('form')];
    }

    get activeTier() {
      const radio = this.querySelector('[data-tier-radio]:checked');
      return radio ? radio.closest('[data-offer-tier]') : this.tiers[0];
    }

    get bundleItems() {
      const formId = this.getAttribute('form');
      return Array.from(document.querySelectorAll(`[form="${formId}"] input[name="bundles"]:checked`)).map((input) => ({
        id: input.value,
        quantity: 1,
        price: input.getAttribute('data-price')
      }));
    }

    activeSizeControl(tier) {
      const field = tier.querySelector('[data-size-field]:not([hidden])');
      if (!field) return tier.querySelector('[data-tier-select]');
      return field.matches('[data-tier-select]') ? field : field.querySelector('[data-tier-select]');
    }

    onChange(event) {
      const target = event.target;
      if (target.hasAttribute('data-color-select')) {
        this.applyColorChoice(target);
        this.syncActiveTier();
      } else if (target.hasAttribute('data-tier-radio') || target.hasAttribute('data-tier-select')) {
        this.syncActiveTier();
      }
      // "bundles" checkbox changes are handled natively by product-bundle-details.
    }

    applyColorChoice(colorSelect) {
      const tier = colorSelect.closest('[data-offer-tier]');
      const option = colorSelect.selectedOptions[0];
      if (!tier || !option) return;
      const handle = option.value;

      tier.querySelectorAll('[data-size-field]').forEach((field) => {
        field.hidden = field.getAttribute('data-product-handle') !== handle;
        if (field.matches('input[data-tier-select]')) field.disabled = field.hidden;
      });

      const thumb = tier.querySelector('[data-tier-thumb]');
      if (thumb) {
        const src = option.getAttribute('data-image');
        const srcset = option.getAttribute('data-srcset');
        if (src) thumb.src = src;
        if (srcset) thumb.srcset = srcset;
      }

      const priceEl = tier.querySelector('[data-tier-price]');
      const price = parseInt(option.getAttribute('data-price'), 10);
      if (priceEl && !isNaN(price) && window.theme && theme.Currency) {
        priceEl.innerHTML = theme.Currency.formatMoney(price, theme.settings.moneyFormat);
      }
    }

    activeColors(tier) {
      const colorSelect = tier.querySelector('[data-color-select]');
      if (colorSelect && colorSelect.selectedOptions[0]) {
        return colorSelect.selectedOptions[0].getAttribute('data-colors') || '';
      }
      return tier.getAttribute('data-colors') || '';
    }

    updateCrosssell(colors) {
      const rows = Array.from(this.querySelectorAll('[data-crosssell-row]'));
      if (rows.length < 2) return;

      let target = rows.find((row) => colors && row.getAttribute('data-colors') === colors);
      target = target || rows.find((row) => !row.hidden) || rows[0];

      const current = rows.find((row) => !row.hidden);
      if (current === target) return;

      const wasChecked = current && current.querySelector('input[name="bundles"]')?.checked;
      rows.forEach((row) => {
        row.hidden = row !== target;
        const box = row.querySelector('input[name="bundles"]');
        if (box) box.checked = row === target && !!wasChecked;
      });
    }

    updateCrosssellTotal(tier) {
      const row = this.querySelector('[data-crosssell-row]:not([hidden])');
      if (!row) return;
      const totalEl = row.querySelector('[data-crosssell-total]');
      if (!totalEl) return;

      const tierSave = parseInt(tier.getAttribute('data-save'), 10) || 0;
      const rowSave = parseInt(row.getAttribute('data-save'), 10) || 0;
      const combined = tierSave + rowSave;

      // Only worth showing when the pack itself also saves — otherwise the
      // row's own save badge already tells the whole story.
      if (tierSave > 0 && combined > 0 && window.theme && theme.Currency) {
        const amount = theme.Currency.formatMoney(combined, theme.settings.moneyFormat);
        totalEl.innerHTML = (totalEl.getAttribute('data-text') || 'Total saving with this set: [amount]').replace('[amount]', amount);
        totalEl.hidden = false;
      } else {
        totalEl.hidden = true;
      }
    }

    syncActiveTier() {
      const tier = this.activeTier;
      if (!tier) return;

      this.tiers.forEach((el) => el.classList.toggle('is-active', el === tier));

      this.updateCrosssell(this.activeColors(tier));
      this.updateCrosssellTotal(tier);

      const control = this.activeSizeControl(tier);
      if (!control) return;

      const option = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
      if (!option) return;

      const variantId = option.value;
      const price = parseInt(option.getAttribute('data-price'), 10) || 0;

      const form = this.form;
      if (form) {
        const idInput = form.querySelector('[name="id"]');
        if (idInput) {
          idInput.value = variantId;
          idInput.disabled = false;
        }

        // Update every buy-price bound to this form and let it recompute
        // (base price + checked bundle items) via the native event.
        // NB: form.id would return the input[name="id"] (named-property
        // shadowing), so use the ladder's own form attribute instead.
        document.querySelectorAll(`product-buy-price[form="${this.getAttribute('form')}"]`).forEach((el) => {
          el.setAttribute('data-price', price);
        });
        form.dispatchEvent(new CustomEvent('bundle:change', { detail: { items: this.bundleItems } }));
      }

      // Sticky bar variant label.
      const stickyOptions = document.querySelector('[data-sticky-product-options]');
      if (stickyOptions) {
        const title = control.getAttribute('data-product-title') || tier.getAttribute('data-product-title') || '';
        const variantTitle = control.tagName === 'SELECT' ? option.textContent.trim() : '';
        stickyOptions.textContent = variantTitle ? `${title} — ${variantTitle}` : title;
      }

      this.updateStock(option);
    }

    updateStock(option) {
      const stockEl = this.querySelector('[data-urgency-stock]');
      const urgencyEl = this.querySelector('[data-urgency]');
      if (!urgencyEl) return;

      let stockVisible = false;
      if (stockEl) {
        const qty = parseInt(option.getAttribute('data-qty'), 10);
        const threshold = parseInt(stockEl.getAttribute('data-threshold'), 10) || 10;
        if (!isNaN(qty) && qty > 0 && qty <= threshold) {
          stockEl.textContent = (stockEl.getAttribute('data-text') || 'Only [count] left in stock!').replace('[count]', qty);
          stockEl.hidden = false;
          stockVisible = true;
        } else {
          stockEl.hidden = true;
        }
      }

      const hasCountdown = !!this.querySelector('[data-urgency-countdown]');
      urgencyEl.hidden = !hasCountdown && !stockVisible;
    }

    startCountdown() {
      const target = this.querySelector('[data-countdown]');
      const urgencyEl = this.querySelector('[data-urgency]');
      if (!target) {
        if (urgencyEl && this.querySelector('[data-urgency-stock]')) urgencyEl.hidden = false;
        return;
      }
      if (urgencyEl) urgencyEl.hidden = false;

      const tick = () => {
        const now = new Date();
        const midnight = new Date(now);
        midnight.setHours(24, 0, 0, 0);
        const diff = Math.max(0, midnight - now);
        const h = String(Math.floor(diff / 3600000)).padStart(2, '0');
        const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
        const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
        target.textContent = `${h}:${m}:${s}`;
      };
      tick();
      this.countdownInterval = setInterval(tick, 1000);
    }
  }

  customElements.define('reeva-offer-ladder', ReevaOfferLadder);
})();
